import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CalendarDays, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, XCircle, 
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, 
  Filter, Eye, X, LayoutGrid, Layers, TrendingUp, FileText, List, LayoutList, Clock, MapPin, Building
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { CustomDatePicker } from '../../shared/components/custom';
import {
  formatPointageDate,
  formatPointageDateWithHijri,
  HOLIDAY_TYPE_VALUES,
  HOLIDAY_CATEGORY_VALUES,
  SAUDI_PUBLIC_HOLIDAY_TEMPLATES,
  saudiFixedHolidayDate,
  normalizeHolidayType,
  normalizeHolidayCategory,
} from './pointageHelpers';

const CalendrierFeriesPage = () => {
  const { t, i18n } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.calendrierFeriesPage.${key}`, opts);

  const TYPE_LABEL_KEYS = {
    [HOLIDAY_TYPE_VALUES.national]: 'type_national',
    [HOLIDAY_TYPE_VALUES.religious]: 'type_religious',
    [HOLIDAY_TYPE_VALUES.company]: 'type_company',
    [HOLIDAY_TYPE_VALUES.regional]: 'type_regional',
    National: 'type_national',
    Religieux: 'type_religious',
    Entreprise: 'type_company',
    'Régional': 'type_regional',
  };
  const getTypeLabel = (value) => {
    const normalized = normalizeHolidayType(value);
    const key = TYPE_LABEL_KEYS[normalized] || TYPE_LABEL_KEYS[value];
    return key ? p(key) : value || '-';
  };

  const CATEGORY_LABEL_KEYS = {
    [HOLIDAY_CATEGORY_VALUES.paid]: 'category_paid',
    [HOLIDAY_CATEGORY_VALUES.unpaid]: 'category_unpaid',
    [HOLIDAY_CATEGORY_VALUES.bridge]: 'category_bridge',
    'Férié Payé': 'category_paid',
    'Férié Non Payé': 'category_unpaid',
    Pont: 'category_bridge',
  };
  const getCategoryLabel = (value) => {
    const normalized = normalizeHolidayCategory(value);
    const key = CATEGORY_LABEL_KEYS[normalized] || CATEGORY_LABEL_KEYS[value];
    return key ? p(key) : value || '-';
  };

  const formatDate = (dateStr) => formatPointageDateWithHijri(dateStr, '-');

  const formatYesNo = (value) => (value ? p('yes') : p('no'));

  const [searchTerm, setSearchTerm] = useState('');
  const [feries, setFeries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const FERIE_FORM_FIELD_COUNT = 10;

  const ferieFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_holiday_info'), icon: CalendarDays, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_application_notes_form'), icon: Building, iconClass: 'text-orange-400' },
    ],
  }, FERIE_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({ 
    holiday_date: '',
    holiday_name: '',
    holiday_type: '',
    category: '',
    is_recurring: '',
    is_paid: '',
    applicable_sites: '',
    description: '',
    year: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_holidays');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadFeries(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadFeries = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/feries');
      const rows = response?.data || response || [];
      setFeries(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading feries:', error);
      setFeries([]);
    }
  };

  const currentYear = new Date().getFullYear();
  const stats = {
    total: feries.length,
    thisYear: feries.filter(f => parseInt(f.year) === currentYear).length,
    national: feries.filter(f => normalizeHolidayType(f.holiday_type) === HOLIDAY_TYPE_VALUES.national).length,
    religious: feries.filter(f => normalizeHolidayType(f.holiday_type) === HOLIDAY_TYPE_VALUES.religious).length,
    remaining: feries.filter(f => new Date(f.holiday_date) > new Date() && parseInt(f.year) === currentYear).length
  };

  const mainTabs = [
    { id: 'all_holidays', name: t('common.general_view'), icon: LayoutGrid },
    { id: 'by_type', name: p('tab_by_type'), icon: CalendarDays },
    { id: 'by_category', name: p('tab_by_category'), icon: Layers }
  ];

  const quickFilterButtons = [
    { id: 'all', name: t('common.all'), icon: Layers },
    { id: HOLIDAY_TYPE_VALUES.national, name: p('type_national'), icon: Building },
    { id: HOLIDAY_TYPE_VALUES.religious, name: p('type_religious'), icon: CalendarDays },
    { id: HOLIDAY_TYPE_VALUES.company, name: p('type_company'), icon: Building },
    { id: 'recurring', name: p('filter_recurring'), icon: TrendingUp }
  ];

  const getTypeDistribution = () => {
    const national = feries.filter(f => normalizeHolidayType(f.holiday_type) === HOLIDAY_TYPE_VALUES.national).length;
    const religious = feries.filter(f => normalizeHolidayType(f.holiday_type) === HOLIDAY_TYPE_VALUES.religious).length;
    const entreprise = feries.filter(f => normalizeHolidayType(f.holiday_type) === HOLIDAY_TYPE_VALUES.company).length;
    const regional = feries.filter(f => normalizeHolidayType(f.holiday_type) === HOLIDAY_TYPE_VALUES.regional).length;
    
    return [
      { filterKey: HOLIDAY_TYPE_VALUES.national, name: p('type_national'), count: national, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
      { filterKey: HOLIDAY_TYPE_VALUES.religious, name: p('type_religious'), count: religious, color: "bg-gradient-to-r from-green-500 to-green-600" },
      { filterKey: HOLIDAY_TYPE_VALUES.company, name: p('type_company'), count: entreprise, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
      { filterKey: HOLIDAY_TYPE_VALUES.regional, name: p('type_regional'), count: regional, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
    ];
  };

  const getCategoryDistribution = () => {
    const paid = feries.filter(f => normalizeHolidayCategory(f.category) === HOLIDAY_CATEGORY_VALUES.paid).length;
    const unpaid = feries.filter(f => normalizeHolidayCategory(f.category) === HOLIDAY_CATEGORY_VALUES.unpaid).length;
    const bridge = feries.filter(f => normalizeHolidayCategory(f.category) === HOLIDAY_CATEGORY_VALUES.bridge).length;
    
    return [
      { filterKey: HOLIDAY_CATEGORY_VALUES.paid, name: p('category_paid'), count: paid, color: "bg-gradient-to-r from-green-500 to-green-600" },
      { filterKey: HOLIDAY_CATEGORY_VALUES.unpaid, name: p('category_unpaid'), count: unpaid, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
      { filterKey: HOLIDAY_CATEGORY_VALUES.bridge, name: p('category_bridge'), count: bridge, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
    ];
  };

  const feriesDistribution = {
    all_holidays: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: HOLIDAY_TYPE_VALUES.national, name: p('type_national'), count: stats.national, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: HOLIDAY_TYPE_VALUES.religious, name: p('type_religious'), count: stats.religious, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'this_year', name: p('dist_this_year'), count: stats.thisYear, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'remaining', name: p('dist_remaining'), count: stats.remaining, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    },
    by_type: {
      title: p('dist_by_type_title'),
      data: getTypeDistribution()
    },
    by_category: {
      title: p('dist_by_category_title'),
      data: getCategoryDistribution()
    }
  };

  const distributionInfo = feriesDistribution[mainViewMode] || feriesDistribution.all_holidays;

  
  const baseFilteredFeries = useMemo(() => feries.filter(f => {
    if (statusFilter !== 'all' && f.holiday_type !== statusFilter) return false;
    
    if (dateRange.start || dateRange.end) {
      const holidayDate = new Date(f.holiday_date);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (holidayDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (holidayDate > endOfDay) return false;
      }
    }
    
    if (searchTerm && !f.holiday_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !f.holiday_type?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_holidays') {
      if (quickFilter === HOLIDAY_TYPE_VALUES.national && normalizeHolidayType(f.holiday_type) !== HOLIDAY_TYPE_VALUES.national) return false;
      if (quickFilter === HOLIDAY_TYPE_VALUES.religious && normalizeHolidayType(f.holiday_type) !== HOLIDAY_TYPE_VALUES.religious) return false;
      if (quickFilter === HOLIDAY_TYPE_VALUES.company && normalizeHolidayType(f.holiday_type) !== HOLIDAY_TYPE_VALUES.company) return false;
      if (quickFilter === 'recurring' && !f.is_recurring) return false;
    }
    
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_holidays':
          if (activeDistributionFilter === HOLIDAY_TYPE_VALUES.national && normalizeHolidayType(f.holiday_type) !== HOLIDAY_TYPE_VALUES.national) return false;
          if (activeDistributionFilter === HOLIDAY_TYPE_VALUES.religious && normalizeHolidayType(f.holiday_type) !== HOLIDAY_TYPE_VALUES.religious) return false;
          if (activeDistributionFilter === 'this_year' && parseInt(f.year) !== currentYear) return false;
          if (activeDistributionFilter === 'remaining' && !(new Date(f.holiday_date) > new Date() && parseInt(f.year) === currentYear)) return false;
          break;
        case 'by_type':
          if (f.holiday_type !== activeDistributionFilter) return false;
          break;
        case 'by_category':
          if (f.category !== activeDistributionFilter) return false;
          break;
        default:
          break;
      }
    }
    
    return true;
  }), [feries, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, currentYear]);

  const columnFilterRules = useMemo(() => [
      textColumnRule('holiday_date', (f) => formatPointageDate(f.holiday_date) || ''),
      textColumnRule('holiday_name', (f) => f.holiday_name || ''),
      textColumnRule('holiday_type', (f) => getTypeLabel(f.holiday_type)),
      textColumnRule('category', (f) => getCategoryLabel(f.category)),
      textColumnRule('recurring', (f) => String(f.is_recurring ?? f.recurring ?? '')),
      textColumnRule('paid', (f) => String(f.is_paid ?? f.paid ?? '')),
      textColumnRule('sites', (f) => f.sites || f.site_names || ''),
    ], []);

  const filteredFeries = useMemo(
    () => applyColumnFilters(baseFilteredFeries, colFilters, columnFilterRules),
    [baseFilteredFeries, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        ...formData,
        is_recurring: formData.is_recurring === 'true' || formData.is_recurring === true,
        is_paid: formData.is_paid === 'true' || formData.is_paid === true,
        applicable_sites: typeof formData.applicable_sites === 'string' 
          ? formData.applicable_sites.split(',').map(s => s.trim()).filter(Boolean)
          : formData.applicable_sites || []
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/feries/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/feries', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      resetForm();
      loadFeries();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      ...record,
      applicable_sites: Array.isArray(record.applicable_sites) 
        ? record.applicable_sites.join(', ') 
        : record.applicable_sites || '',
      is_recurring: record.is_recurring ? 'true' : 'false',
      is_paid: record.is_paid ? 'true' : 'false'
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/pointage/feries/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadFeries();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/feries/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadFeries();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      holiday_date: '',
      holiday_name: '',
      holiday_type: '',
      category: '',
      is_recurring: '',
      is_paid: '',
      applicable_sites: '',
      description: '',
      year: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredFeries);


  const getCategoryBadge = (category) => {
    const normalized = normalizeHolidayCategory(category);
    const style = normalized === HOLIDAY_CATEGORY_VALUES.paid ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                  normalized === HOLIDAY_CATEGORY_VALUES.unpaid ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                  'bg-purple-500/20 text-purple-400 border-purple-500/30';
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${style}`}>{getCategoryLabel(category)}</span>;
  };

  const seedSaudiGregorianHolidays = async () => {
    const year = new Date().getFullYear();
    const templates = SAUDI_PUBLIC_HOLIDAY_TEMPLATES.filter((t) => t.calendar === 'gregorian');
    try {
      await Promise.all(templates.map((t) => localApiAdapter.post('/pointage/feries', {
        holiday_name: p(t.nameKey),
        holiday_date: saudiFixedHolidayDate(year, t.month, t.day),
        holiday_type: t.holiday_type,
        category: t.category,
        is_recurring: true,
        is_paid: true,
        year: String(year),
        description: p('saudi_holiday_note'),
      })));
      loadFeries();
    } catch (error) {
      console.error('Error seeding Saudi holidays:', error);
    }
  };

  const POINTAGE_TABLE_COLUMNS = [
    { key: 'holiday_date', labelKey: 'col_date' },
    { key: 'holiday_name', labelKey: 'col_name' },
    { key: 'holiday_type', labelKey: 'col_type' },
    { key: 'category', labelKey: 'col_category' },
    { key: 'recurring', labelKey: 'col_recurring' },
    { key: 'paid', labelKey: 'col_paid' },
    { key: 'sites', labelKey: 'col_sites' },
    { key: 'actions', label: t('common.actions'), required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, t, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <CalendarDays className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadFeries} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, label: `${t('common.delete')} (${selectedRecords.length})`, title: t('common.delete', { defaultValue: 'حذف' }) })}
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{t('common.advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button type="button" onClick={seedSaudiGregorianHolidays} className="bg-white/5 border border-white/20 hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2">
            <CalendarDays className="h-5 w-5" /><span>{p('seed_saudi_holidays')}</span>
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setFormTab('general'); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_holiday')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: CalendarDays, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_this_year'), value: stats.thisYear, icon: Calendar, color: 'from-green-500 to-green-600' },
          { title: p('stat_national'), value: stats.national, icon: Building, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_religious'), value: stats.religious, icon: CalendarDays, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_remaining'), value: stats.remaining, icon: Clock, color: 'from-pink-500 to-pink-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_holidays' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    return p('pct_of_total', { pct });
                  })()}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })} deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })} />
                  {isColVisible('holiday_date') && (
                    <TableColumnHeaderFilter columnKey="holiday_date" label={p('col_date')} value={colFilters.holiday_date ?? ''} onChange={(v) => setColFilter('holiday_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('holiday_name') && (
                    <TableColumnHeaderFilter columnKey="holiday_name" label={p('col_name')} value={colFilters.holiday_name ?? ''} onChange={(v) => setColFilter('holiday_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('holiday_type') && (
                    <TableColumnHeaderFilter columnKey="holiday_type" label={p('col_type')} value={colFilters.holiday_type ?? ''} onChange={(v) => setColFilter('holiday_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('category') && (
                    <TableColumnHeaderFilter columnKey="category" label={p('col_category')} value={colFilters.category ?? ''} onChange={(v) => setColFilter('category', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('recurring') && (
                    <TableColumnHeaderFilter columnKey="recurring" label={p('col_recurring')} value={colFilters.recurring ?? ''} onChange={(v) => setColFilter('recurring', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('paid') && (
                    <TableColumnHeaderFilter columnKey="paid" label={p('col_paid')} value={colFilters.paid ?? ''} onChange={(v) => setColFilter('paid', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('sites') && (
                    <TableColumnHeaderFilter columnKey="sites" label={p('col_sites')} value={colFilters.sites ?? ''} onChange={(v) => setColFilter('sites', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredFeries.length > 0 ? (
                  filteredFeries.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('holiday_date') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{formatDate(record.holiday_date)}</span></td>
                      )}
                      {isColVisible('holiday_name') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.holiday_name}</span></td>
                      )}
                      {isColVisible('holiday_type') && (
                        <td className="px-6 py-4"><div className="flex justify-center">{getTypeBadge(record.holiday_type)}</div></td>
                      )}
                      {isColVisible('category') && (
                        <td className="px-6 py-4"><div className="flex justify-center">{getCategoryBadge(record.category)}</div></td>
                      )}
                      {isColVisible('recurring') && (
                        <td className="px-6 py-4 text-center">{record.is_recurring ? <CheckCircle size={18} className="text-green-400 mx-auto" /> : <XCircle size={18} className="text-gray-400 mx-auto" />}</td>
                      )}
                      {isColVisible('paid') && (
                        <td className="px-6 py-4 text-center">{record.is_paid ? <CheckCircle size={18} className="text-green-400 mx-auto" /> : <XCircle size={18} className="text-gray-400 mx-auto" />}</td>
                      )}
                      {isColVisible('sites') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{Array.isArray(record.applicable_sites) ? record.applicable_sites.join(', ') : record.applicable_sites || '-'}</span></td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><CalendarDays size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredFeries.length })}</span>
                      <span>{p('footer_paid')} <span className="text-white font-semibold">{filteredFeries.filter(f => f.is_paid).length}</span></span>
                      <span>{p('footer_recurring')} <span className="text-white font-semibold">{filteredFeries.filter(f => f.is_recurring).length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredFeries.length > 0 ? (
              filteredFeries.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><CalendarDays size={20} className="text-emerald-400" />{record.holiday_name}</h3>
                        <div className="flex">{getTypeBadge(record.holiday_type)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_basic_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_date')}</span> {formatDate(record.holiday_date)}</div>
                            <div><span className="font-medium">{p('label_year')}</span> {record.year}</div>
                            <div><span className="font-medium">{p('label_type')}</span> {getTypeLabel(record.holiday_type)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_category_status')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{getCategoryBadge(record.category)}</div>
                            <div><span className="font-medium">{p('label_recurring')}</span> {formatYesNo(record.is_recurring)}</div>
                            <div><span className="font-medium">{p('label_paid')}</span> {formatYesNo(record.is_paid)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_application')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_sites')}</span> {Array.isArray(record.applicable_sites) ? record.applicable_sites.join(', ') : record.applicable_sites || p('all_sites')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_notes')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{record.description || p('no_description')}</div>
                            {record.notes && <div className="mt-2 text-xs text-white/50">{record.notes}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><CalendarDays size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="calendrier-feries-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><CalendarDays size={18} className="text-emerald-400" />{p('section_main_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('label_name')}</p><p className={modalForm.viewValue}>{viewingRecord.holiday_name}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('col_date')}</p><p className={modalForm.viewValue}>{formatDate(viewingRecord.holiday_date)}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('field_year')}</p><p className={modalForm.viewValue}>{viewingRecord.year}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_type_category')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('col_type')}</p>{getTypeBadge(viewingRecord.holiday_type)}</div>
                      <div><p className={modalForm.viewLabel}>{p('col_category')}</p>{getCategoryBadge(viewingRecord.category)}</div>
                      <div><p className={modalForm.viewLabel}>{p('col_recurring')}</p><p className={modalForm.viewValue}>{formatYesNo(viewingRecord.is_recurring)}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('col_paid')}</p><p className={modalForm.viewValue}>{formatYesNo(viewingRecord.is_paid)}</p></div>
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('section_application_notes')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('label_applicable_sites')}</p><p className={modalForm.viewValue}>{Array.isArray(viewingRecord.applicable_sites) ? viewingRecord.applicable_sites.join(', ') : viewingRecord.applicable_sites || p('all_sites')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('label_description')}</p><p className={modalForm.viewValue}>{viewingRecord.description || '-'}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('field_notes')}</p><p className={modalForm.viewValue}>{viewingRecord.notes || '-'}</p></div>
                    </div>
                  </div>
                </div>
              </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={Calendar}
          onSubmit
          formId="ferie-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          maxWidth="1200px"
          testId="calendrier-feries-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={ferieFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="ferie-form"
            onSubmit={handleSubmit}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
          >
            {pointageFormTabVisible(ferieFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_holiday_name')} />}</label><input type="text" required value={formData.holiday_name} onChange={(e) => setFormData({ ...formData, holiday_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_holiday_name')} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_date')} />}</label><CustomDatePicker value={formData.holiday_date} onChange={(value) => setFormData({ ...formData, holiday_date: value })} placeholder={p('ph_select_date')} /></div>
                <div><label className={modalForm.label}>{p('field_recurring')}</label><CustomSelect value={formData.is_recurring} onChange={(value) => setFormData({ ...formData, is_recurring: value })} options={[{ value: 'true', label: p('yes'), icon: <CheckCircle size={14} className="text-green-400" /> }, { value: 'false', label: p('no'), icon: <XCircle size={14} className="text-gray-400" /> }]} searchable={true} creatable={true} autoSelect={false} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_year')}</label><input type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_year')} /></div>
                <div><label className={modalForm.label}>{p('field_type')}</label><CustomSelect value={formData.holiday_type} onChange={(value) => setFormData({ ...formData, holiday_type: value })} options={typeOptions.map(o => ({ value: o.value, label: o.label, icon: o.icon }))} searchable={true} creatable={true} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{p('field_category')}</label><CustomSelect value={formData.category} onChange={(value) => setFormData({ ...formData, category: value })} options={categoryOptions.map(o => ({ value: o.value, label: o.label, icon: o.icon }))} searchable={true} creatable={true} autoSelect={false} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_paid')}</label><CustomSelect value={formData.is_paid} onChange={(value) => setFormData({ ...formData, is_paid: value })} options={[{ value: 'true', label: p('yes'), icon: <CheckCircle size={14} className="text-green-400" /> }, { value: 'false', label: p('no'), icon: <XCircle size={14} className="text-gray-400" /> }]} searchable={true} creatable={true} autoSelect={false} /></div>
              </div>
              </div>
            )}
            {pointageFormTabVisible(ferieFormTabs, formTab, 'details') && (
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('field_applicable_sites')}</label><input type="text" value={formData.applicable_sites} onChange={(e) => setFormData({ ...formData, applicable_sites: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_sites')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('field_description')}</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_description')} /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('field_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} /></div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default CalendrierFeriesPage;

